import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'

import AddressInputSection from './AddressInputSection'

import { getAddressList, deleteAddress, updateAddress, postAddress } from '../../api/orderHistory'
import { AddressList, AddressItem } from '../../@types/order'

type AddressListModalProps = {
  visible: boolean
  addressNo?: number
  setUserDefaultAddress: (address: AddressItem | null) => void
  onClose: () => void
  onComplete?: (data: any) => void
}

const AddressListModal = ({
  visible,
  addressNo,
  setUserDefaultAddress,
  onClose,
  onComplete,
}: AddressListModalProps) => {
  const [addAddress, setAddAddress] = useState<boolean>(false)
  const [editAddress, setEditAddress] = useState<AddressItem | null>(null)
  const [defaultAddress, setDefaultAddress] = useState<AddressItem | null>(null)
  const [bookedAddresses, setBookedAddresses] = useState<AddressItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const addressInputRef = useRef<{ validate: () => boolean; getValues: () => any }>(null)

  const refreshAddressList = async () => {
    setIsLoading(true)
    try {
      const data: AddressList = await getAddressList()
      setDefaultAddress(data.defaultAddress)
      setUserDefaultAddress(data.defaultAddress)
      setBookedAddresses(data.bookedAddresses.filter((item) => item.defaultYn !== 'Y'))
    } finally {
      setIsLoading(false)
    }
  }

  const addAddressHandler = async () => {
    if (!addAddress || !addressInputRef.current) return

    const isValid = addressInputRef.current.validate()
    if (!isValid) return

    try {
      const {
        receiveDefaultAddress,
        receiveAddressName,
        receiveName,
        receiveZip,
        receiveAddress_1,
        receiveAddress_2,
        receivePhone,
        receiveMemo,
        receiveAddressType,
      } = addressInputRef.current.getValues()

      const value = {
        defaultYn: receiveDefaultAddress === 'Y' ? 'Y' : 'N',
        addressName: receiveAddressName,
        receiverName: receiveName,
        receiverZipCd: receiveZip,
        receiverAddress: receiveAddress_1,
        receiverDetailAddress: receiveAddress_2,
        receiverContact1: receivePhone,
        addressMemo: receiveMemo,
        receiverJibunAddress: receiveAddressType === 'J' ? receiveAddress_1 : '',
      }
      await postAddress(value)
      await refreshAddressList()
    } catch (e) {
      alert('주소 추가 중 오류가 발생했습니다.')
      console.error(e)
    } finally {
      setAddAddress(false)
    }
  }

  const handleAddClick = () => {
    setAddAddress(true)
    setEditAddress(null)
  }

  // 삭제 핸들러
  const handleDelete = async (address: AddressItem) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      await deleteAddress(address.addressNo)
      await refreshAddressList()
    }
  }

  // 수정 핸들러
  const handleEdit = async (address: AddressItem) => {
    setEditAddress(address)
    setAddAddress(false)
  }

  // 주소 추가/수정 완료 핸들러
  const handleAddressSubmit = async (addressNo: number) => {
    if (!addAddress && addressInputRef.current) {
      const isValid = addressInputRef.current.validate()
      if (!isValid) return // 유효성 실패 시 중단

      const {
        receiveDefaultAddress,
        receiveAddressName,
        receiveName,
        receiveZip,
        receiveAddress_1,
        receiveAddress_2,
        receivePhone,
        receiveMemo,
      } = addressInputRef.current.getValues()

      const value = {
        defaultYn: receiveDefaultAddress === 'Y' ? 'Y' : 'N',
        addressName: receiveAddressName,
        receiverName: receiveName,
        receiverZipCd: receiveZip,
        receiverAddress: receiveAddress_1,
        receiverDetailAddress: receiveAddress_2,
        receiverContact1: receivePhone,
        addressMemo: receiveMemo,
        addressNo: addressNo,
      }

      await updateAddress(value, addressNo)
    }
    await refreshAddressList()
    setAddAddress(false)
    setEditAddress(null)
  }

  useEffect(() => {
    document.body.style.overflow = visible ? 'hidden' : ''
    if (visible) {
      refreshAddressList()
    } else {
      // 팝업이 닫힐 때 상태 초기화
      setAddAddress(false)
      setEditAddress(null)
      setDefaultAddress(null)
      setBookedAddresses([])
      setIsLoading(false)
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [visible])

  if (!visible) return null // 이 줄을 추가하세요!

  return (
    <Dim>
      <PopupWrap>
        <PopupHeader>
          <button title='닫기' onClick={onClose}>
            <img src='/images/icon_close.png' alt='닫기' />
          </button>
        </PopupHeader>
        <PopupContent>
          {!addAddress && !editAddress && (
            <AddAddressButtonFull onClick={handleAddClick}>배송지 신규 추가</AddAddressButtonFull>
          )}

          {/* 추가 폼: 추가 모드일 때만 노출 */}
          {addAddress && !editAddress && (
            <FormButtonWrap>
              <AddressInputSection ref={addressInputRef} addAddress={true} defaultAddress={defaultAddress} />
              <ButtonRow>
                <CancelButton type='button' onClick={() => setAddAddress(false)}>
                  취소
                </CancelButton>
                <AddAddressButton onClick={addAddressHandler}>배송지 추가하기</AddAddressButton>
              </ButtonRow>
            </FormButtonWrap>
          )}

          <ul>
            {isLoading ? (
              <LoadingWrap>
                <LoadingSpinner />
                <span>주소 목록을 불러오는 중입니다...</span>
              </LoadingWrap>
            ) : (
              <>
                {defaultAddress && (
                  <AddressCard>
                    {editAddress && editAddress.addressNo === defaultAddress.addressNo ? (
                      <FormButtonWrap>
                        <AddressInputSection
                          ref={addressInputRef}
                          addAddress={true}
                          editAddress={editAddress}
                          defaultAddress={defaultAddress}
                        />
                        <ButtonRow>
                          <CancelButton type='button' onClick={() => setEditAddress(null)}>
                            취소
                          </CancelButton>
                          <AddAddressButton onClick={() => handleAddressSubmit(defaultAddress.addressNo)}>
                            수정 완료
                          </AddAddressButton>
                        </ButtonRow>
                      </FormButtonWrap>
                    ) : (
                      <StyledAddressInfo>
                        {defaultAddress.addressNo === addressNo && (
                          <div style={{ color: '#2d7cff', fontWeight: 'bold', marginBottom: '8px' }}>
                            선택된 배송지입니다.
                          </div>
                        )}
                        <div>
                          <AddressName>{defaultAddress.addressName} (기본 배송지)</AddressName>
                          <AddressReceiverName>{defaultAddress.receiverName}</AddressReceiverName>
                          <AddressText>{defaultAddress.receiverZipCd}</AddressText>
                          <AddressText>
                            {defaultAddress.receiverAddress} {defaultAddress.receiverDetailAddress}
                          </AddressText>
                        </div>
                        <ButtonRow>
                          <SelectButton onClick={() => onComplete?.(defaultAddress)}>선택</SelectButton>
                          <EditButton onClick={() => handleEdit(defaultAddress)}>수정</EditButton>
                        </ButtonRow>
                      </StyledAddressInfo>
                    )}
                  </AddressCard>
                )}
                {bookedAddresses.length > 0 &&
                  bookedAddresses.map((address) => (
                    <AddressCard key={address.addressNo}>
                      {editAddress && editAddress.addressNo === address.addressNo ? (
                        <FormButtonWrap>
                          <AddressInputSection
                            ref={addressInputRef}
                            addAddress={true}
                            editAddress={editAddress}
                            defaultAddress={defaultAddress}
                          />
                          <ButtonRow>
                            <CancelButton type='button' onClick={() => setEditAddress(null)}>
                              취소
                            </CancelButton>
                            <AddAddressButton onClick={() => handleAddressSubmit(address.addressNo)}>
                              수정 완료
                            </AddAddressButton>
                          </ButtonRow>
                        </FormButtonWrap>
                      ) : (
                        <StyledAddressInfo>
                          {address.addressNo === addressNo && (
                            <div style={{ color: '#2d7cff', fontWeight: 'bold', marginBottom: '8px' }}>
                              선택된 배송지입니다.
                            </div>
                          )}
                          <div>
                            <AddressName>{address.addressName}</AddressName>
                            <AddressReceiverName>{address.receiverName}</AddressReceiverName>
                            <AddressText>{address.receiverZipCd}</AddressText>
                            <AddressText>
                              {address.receiverAddress} {address.receiverDetailAddress}
                            </AddressText>
                          </div>
                          <ButtonRow>
                            <SelectButton onClick={() => onComplete?.(address)}>선택</SelectButton>
                            <EditButton onClick={() => handleEdit(address)}>수정</EditButton>
                            <DeleteButton onClick={() => handleDelete(address)}>삭제</DeleteButton>
                          </ButtonRow>
                        </StyledAddressInfo>
                      )}
                    </AddressCard>
                  ))}
                {!defaultAddress && bookedAddresses.length === 0 && (
                  <div style={{ marginTop: '10px', textAlign: 'center', color: '#999', padding: '40px 20px' }}>
                    등록된 배송지가 없습니다. 배송지를 추가해 주세요.
                  </div>
                )}
              </>
            )}
          </ul>
        </PopupContent>
      </PopupWrap>
    </Dim>
  )
}

const Dim = styled.div`
  position: fixed;
  inset: 0;
  width: 100dvw;
  height: 100dvh;
  background: rgba(0, 0, 0, 0.6);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`
const PopupWrap = styled.div`
  background: var(--colWhite, #fff);
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  width: 98dvw;
  max-width: 480px;
  min-width: 320px;
  min-height: 120px;
  max-height: 90vh;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const PopupHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 5px 0 0;
  background: transparent;
  button {
    background: none;
    border: none;
    cursor: pointer;
    width: 48px;
    height: 48px;
    padding: 12px;
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
`

const PopupContent = styled.div`
  width: 100%;
  height: 100%;
  padding: 20px 12px;
  box-sizing: border-box;
  border-top: 1px solid var(--colGray3);
  background: var(--colWhite, #fff);
  overflow-y: auto;
`

const AddressCard = styled.li`
  background: #fafbfc;
  border: 1px solid #e3e5e8;
  border-radius: 8px;
  padding: 16px 12px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StyledAddressInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const AddressName = styled.div`
  font-weight: bold;
  font-size: 17px;
  color: #222;
  margin-bottom: 12px;
`

const AddressReceiverName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #666;
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`

const AddressText = styled.div`
  font-size: 15px;
  color: #444;
  margin-bottom: 2px;
`

const FormButtonWrap = styled.div`
  margin-bottom: 16px;
`

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`

const CancelButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  background: #eee;
  border: 1px solid var(--grey3, #ccc);
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  color: #333;
  transition: background 0.15s;
  &:hover {
    background: #ddd;
  }
`

const AddAddressButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  background: #fff;
  border: 1px solid var(--grey3, #ccc);
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.15s;
  &:hover {
    background: var(--colGray2, #f5f5f5);
  }
`

const AddAddressButtonFull = styled(AddAddressButton)`
  width: 100%;
  margin-bottom: 16px;
`

const SelectButton = styled.button`
  flex: 1;
  background: #2d7cff;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 8px 0;
  font-size: 15px;
  cursor: pointer;
  &:hover {
    background: #1b5fcc;
  }
`
const EditButton = styled.button`
  flex: 1;
  background: #fff;
  color: #bfa700;
  border: 1px solid #ffe066;
  border-radius: 5px;
  padding: 8px 0;
  font-size: 15px;
  cursor: pointer;
  &:hover {
    background: #fff3bf;
  }
`
const DeleteButton = styled.button`
  flex: 1;
  background: #fff;
  color: #ff4d4f;
  border: 1px solid #ffb3b3;
  border-radius: 5px;
  padding: 8px 0;
  font-size: 15px;
  cursor: pointer;
  &:hover {
    background: #fff0f0;
  }
`

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #888;
  font-size: 16px;
  gap: 16px;
`

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 4px solid #e3e5e8;
  border-top: 4px solid #2d7cff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
`

export default AddressListModal
